import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from '../../../contexts/translation';
import {
  PerformanceComment,
  PerformanceCommentCategory,
  PerformanceCommentCreateRequest,
} from '../../../types/app/performanceComment.types';
import * as commentApi from '../../../api/app/performanceCommentApi';
import * as commentCategoryApi from '../../../api/app/performanceCommentCategoryApi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Asset } from '../../../types/app/asset.types';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { PerformanceCommentForm } from './PerformanceCommentForm';
import React from 'react-router-dom';
import { Loader } from '../../core/Loader/Loader';
import { DateTimeIntegerProcess } from '../../../types/app/dataProcess';
import { PerformanceCommentTable } from './PerformanceCommentTable';
import { Modal, Box } from '@mui/material';
import {
  dateTimeShowFormat,
  hoursMinutesShowFormat,
  utcFormat,
} from '../../../types/core/date.types';
import { CustomDialog } from '../../core/Dialogs/CustomDialog';
interface Props {
  open: boolean;
  setOpen: any;
  processDataObject: DateTimeIntegerProcess;
  asset: Asset;
  granularity: number;
  timezone: string;
  setRefetch?: any;
}

export function PerformanceCommentModal({
  processDataObject,
  open,
  setOpen,
  asset,
  granularity,
  timezone,
  setRefetch,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [commentList, setCommentList] = useState<PerformanceComment[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: mutateCreatePerformanceComment } = useMutation(commentApi.createItem);

  const { data: commentCategoryList } = useQuery<PerformanceCommentCategory[]>(
    ['commentCategory', asset.id],
    () => commentCategoryApi.getList(asset.id),
  );

  useEffect(() => {
    if (!processDataObject || open === false) return;

    commentApi
      .getListFiltered({
        assetId: asset.id,
        from: processDataObject.timeObject!.clone().utc().format(utcFormat),
        to: processDataObject.timeObject!.clone().utc().format(utcFormat),
      })
      .then((res: any) => setCommentList(res));
  }, [open]);

  const handleOnSubmitCreate: SubmitHandler<PerformanceCommentCreateRequest> = (
    data: PerformanceCommentCreateRequest,
  ) => {
    const requestData = {
      ...data,
      assetId: asset.id,
      assetName: asset?.name,
      time: processDataObject.time,
      category: selectedCategory,
    };

    mutateCreatePerformanceComment(
      { ...requestData },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.dashboard.performance-comment.item-created'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['currentShiftComments']);
          queryClient.invalidateQueries(['historyShiftComments1']);
          queryClient.invalidateQueries(['historyShiftComments2']);
          if (setRefetch) {
            setRefetch(false);
            setRefetch(true);
          }
          setOpen(false);
          setSelectedCategory('');
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (!commentCategoryList) return <Loader />;

  return (
    <>
      <CustomDialog
        title={
          t('page.dashboard.performance-comment.modal-headline') +
          ' ' +
          asset?.name +
          ' - ' +
          processDataObject
            ?.timeObject!.clone()
            .tz(timezone)
            .subtract(granularity, 'minutes')
            .format(dateTimeShowFormat) +
          ' - ' +
          processDataObject?.timeObject!.clone().tz(timezone).format(hoursMinutesShowFormat)
        }
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedCategory('');
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        maxWidth={'lg'}
        modalStyle={{ height: '75vh' }}
      >
        <PerformanceCommentForm
          onSubmit={handleOnSubmitCreate}
          commentCategoryList={commentCategoryList}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          setOpen={setOpen}
        />
        <PerformanceCommentTable data={commentList} />
      </CustomDialog>
    </>
  );
}
